import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Globe,
  Loader2,
  Pause,
  Play,
  Plus,
  RefreshCw,
  ShieldAlert,
  Trash2,
  Wrench,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSubscriptionAccess } from "@/hooks/use-subscription-access";
import { getErrorMessage } from "@/lib/errors";
import { countOf } from "@/lib/trial";
import {
  INCIDENTS_KEY,
  MONITORING_OPTIONS_KEY,
  MONITORS_KEY,
  NOTIFICATION_TARGETS_KEY,
  monitoringApi,
} from "@/lib/monitoring-api";
import { serversApi } from "@/lib/servers-api";
import { SubscriptionNotice } from "@/modules/billing/SubscriptionNotice";
import { MonitorDetailPanel } from "@/modules/monitoring/MonitorDetailPanel";
import { MonitoringSummary } from "@/modules/monitoring/MonitoringSummary";
import {
  MonitorFilters,
  type StatusFilter,
} from "@/modules/monitoring/MonitorFilters";
import { StatusStrip } from "@/modules/monitoring/StatusStrip";
import { groupByHost, matchesSearch } from "@/modules/monitoring/grouping";
import { NotificationTargets } from "@/modules/monitoring/NotificationTargets";
import { relativeTime } from "@/modules/monitoring/format";
import {
  expiryLabel,
  formatUptimePercent,
  isExpiryKind,
  monitorUrgency,
} from "@/modules/monitoring/summary";
import type { Monitor, MonitorStatus } from "@/types/api";

const groupTone: Record<string, "success" | "warning" | "danger" | "muted"> = {
  down: "danger",
  stale: "warning",
  pending: "muted",
  up: "success",
  paused: "muted",
};

const groupLabel: Record<string, string> = {
  down: "Down",
  stale: "Not checked recently",
  pending: "Pending",
  up: "All up",
  paused: "Paused",
};

const statusVariant: Record<
  MonitorStatus,
  "success" | "warning" | "danger" | "muted"
> = {
  up: "success",
  down: "danger",
  pending: "muted",
  paused: "muted",
};

/**
 * Uptime monitoring.
 *
 * ## Staleness is rendered, not hidden
 *
 * `status` is what the platform last observed. If the sweep stops, every monitor goes on reporting
 * its last reading indefinitely and confidently — a wall of green over a fleet that has been down
 * since Tuesday, which is worse than no monitoring at all because the customer acted on it.
 *
 * So a stale monitor is drawn as a warning with the age of the reading, never as its stored status.
 * The server decides what counts as stale; this only has to honour it.
 *
 * ## Polls while something is failing
 *
 * A customer watching a monitor they have just fixed wants to see it come back without reloading.
 * Polling permanently would be a request every ten seconds forever for a page that changes rarely.
 */
/**
 * The five kinds a monitor can be, and what each one needs from the form.
 *
 * Kept as data rather than a chain of conditionals because each kind differs along several axes at
 * once — the label on the address field, the help text that says what to enter, which extra input appears,
 * and how often it makes sense to check. Spreading that across five `kind === '…'` ternaries is how
 * the two-kind version was written, and it did not survive contact with a third.
 */
const KINDS = {
  http: {
    label: "Website or API",
    blurb: "Fetches the page and checks the response.",
    addressLabel: "Address",
    addressHelp:
      "The full address, starting with https:// or http://. A health endpoint is better than the home page, because it can check the database too.",
    defaultInterval: 300,
  },
  tcp: {
    label: "Port",
    blurb:
      "Opens the port and closes it again. For databases and mail servers.",
    addressLabel: "Address",
    addressHelp:
      "The host and port, separated by a colon. Port 22 cannot be monitored; the server page already tests SSH.",
    defaultInterval: 300,
  },
  ssl: {
    label: "SSL certificate",
    blurb:
      "Reads the certificate and fails while there is still time to renew it.",
    addressLabel: "Host",
    addressHelp:
      "The hostname. Port 443 is checked unless you add another after a colon, for a certificate that is not on a web server, such as a mail server on port 993.",
    defaultInterval: 86400,
  },
  dns: {
    label: "DNS record",
    blurb: "Looks the record up and checks it still says what it should.",
    addressLabel: "Name",
    addressHelp: "The exact name to look up, including any subdomain.",
    defaultInterval: 3600,
  },
  domain_expiry: {
    label: "Domain expiry",
    blurb: "Asks the registry when the registration runs out.",
    addressLabel: "Domain",
    addressHelp:
      "The registered domain itself. Only the registration has an expiry date, so a subdomain will be refused.",
    defaultInterval: 86400,
  },
  server: {
    label: "Server health",
    blurb:
      "Watches disk, memory and load on one of your servers, and whether it answers at all.",
    addressLabel: "Server",
    addressHelp:
      "Read from the figures the platform already collects every five minutes, so this costs your server nothing.",
    defaultInterval: 300,
  },
} as const;

type MonitorFormKind = keyof typeof KINDS;

const DNS_RECORD_TYPES = ["A", "AAAA", "CNAME", "MX", "TXT", "NS"] as const;
type DnsRecordType = (typeof DNS_RECORD_TYPES)[number];

export function MonitoringPage() {
  const queryClient = useQueryClient();
  const { permitsChanges, trial } = useSubscriptionAccess();
  const onTrial = trial?.status === "running";

  const [showForm, setShowForm] = useState(false);
  const [kind, setKind] = useState<MonitorFormKind>("http");
  const [name, setName] = useState("");
  const [target, setTarget] = useState("");
  const [intervalSeconds, setIntervalSeconds] = useState(300);
  const [bodyContains, setBodyContains] = useState("");
  const [warnDays, setWarnDays] = useState(30);
  const [recordType, setRecordType] = useState<DnsRecordType>("A");
  const [expectedValue, setExpectedValue] = useState("");
  const [serverId, setServerId] = useState("");
  const [autoFix, setAutoFix] = useState(true);
  const [thresholdDisk, setThresholdDisk] = useState(90);
  const [thresholdMemory, setThresholdMemory] = useState(95);
  const [thresholdLoad, setThresholdLoad] = useState(4);
  const [thresholdCpu, setThresholdCpu] = useState(0);
  const [formError, setFormError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [pendingDelete, setPendingDelete] = useState<Monitor | null>(null);

  const options = useQuery({
    queryKey: MONITORING_OPTIONS_KEY,
    queryFn: () => monitoringApi.options(),
    staleTime: Infinity,
  });

  // Only for the picker: a server monitor names a machine rather than an address, and an
  // HTTP monitor may name one so that a fix has somewhere to run.
  const servers = useQuery({
    queryKey: ["servers", "for-monitoring"],
    queryFn: () => serversApi.list(),
    staleTime: 60_000,
  });

  const monitors = useQuery({
    queryKey: MONITORS_KEY,
    queryFn: () => monitoringApi.list(),
    refetchInterval: (query) =>
      (query.state.data?.monitors ?? []).some(
        (m) => m.status === "down" || m.status === "pending",
      )
        ? 15000
        : false,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: MONITORS_KEY });
    void queryClient.invalidateQueries({ queryKey: INCIDENTS_KEY });
    void queryClient.invalidateQueries({ queryKey: NOTIFICATION_TARGETS_KEY });
  };

  const create = useMutation({
    mutationFn: () =>
      monitoringApi.create({
        kind,
        name: name.trim(),
        target: target.trim(),
        interval_seconds: intervalSeconds,
        // 45 seconds, room for a busy server to answer, and under the interval as the server
        // requires. Thirty reported a cPanel site that answered in 19 to 40 seconds as down.
        timeout_seconds: Math.min(45, intervalSeconds - 1),
        expect_status_from: 100,
        expect_status_to: 399,
        expect_body_contains: kind === "http" ? bodyContains.trim() : "",
        // Sent for every kind; the server builds whichever expectation the kind actually uses and
        // ignores the rest, so the form does not have to assemble a different body each time.
        expect_warn_days: warnDays,
        expect_record_type: kind === "dns" ? recordType : "",
        expect_value: kind === "dns" ? expectedValue.trim() : "",
        // A server monitor IS about a server, so the target and the link are the same id. The
        // other kinds carry it only when the customer has said which machine serves the address.
        server_id: kind === "server" ? target.trim() : serverId,
        auto_fix: autoFix,
        threshold_disk: thresholdDisk,
        threshold_memory: thresholdMemory,
        threshold_load: thresholdLoad,
        threshold_cpu: thresholdCpu,
      }),
    onSuccess: () => {
      setShowForm(false);
      setName("");
      setTarget("");
      setBodyContains("");
      setExpectedValue("");
      setServerId("");
      setFormError(null);
      invalidate();
    },
    onError: (error) => setFormError(getErrorMessage(error)),
  });

  const check = useMutation({
    mutationFn: (id: string) => monitoringApi.check(id),
    onSuccess: invalidate,
  });

  const setPaused = useMutation({
    mutationFn: ({ id, paused }: { id: string; paused: boolean }) =>
      paused ? monitoringApi.pause(id) : monitoringApi.resume(id),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => monitoringApi.remove(id),
    onSuccess: () => {
      setPendingDelete(null);
      invalidate();
    },
  });

  const data = monitors.data;
  const all = data?.monitors ?? [];

  // Counts come from the unfiltered set, so a filter button reports what it would find rather than
  // what is currently on screen.
  const counts: Record<StatusFilter, number> = {
    all: all.length,
    down: all.filter(
      (m) => m.status !== "paused" && (m.is_stale || m.status === "down"),
    ).length,
    up: all.filter((m) => m.status === "up" && !m.is_stale).length,
    paused: all.filter((m) => m.status === "paused").length,
  };

  const visible = all.filter((monitor) => {
    if (!matchesSearch(monitor, search)) return false;
    // "Down" includes stale on purpose: a reading the platform has not refreshed is not something
    // to leave out of the list somebody opened to find what is wrong.
    if (status === "down")
      return (
        monitor.status !== "paused" &&
        (monitor.is_stale || monitor.status === "down")
      );
    if (status === "up") return monitor.status === "up" && !monitor.is_stale;
    if (status === "paused") return monitor.status === "paused";

    return true;
  });

  // Grouped by the host each monitor watches, because a domain with four checks against it is one
  // thing with four opinions rather than four unrelated rows. Within a group, and between groups,
  // anything wrong floats to the top.
  const groups = groupByHost(visible).map((group) => ({
    ...group,
    monitors: [...group.monitors].sort((a, b) => monitorUrgency(a) - monitorUrgency(b)),
  }));
  const limits = options.data;
  const atQuota = data
    ? data.quota.limit > 0 && data.quota.used >= data.quota.limit
    : false;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight">Monitoring</h1>
          <p className="max-w-3xl text-sm text-muted-foreground">
            Checks your sites and ports from outside your servers, and tells you
            when they stop answering.
          </p>
        </div>
        <div className="ml-auto flex items-center gap-4">
          {/* The same used-of-allowed figure the Servers page shows. Only for a real ceiling: -1 is
              no limit, and 0 is an account with no subscription, which the notice below explains. */}
          {data && data.quota.limit > 0 && (
            <div className="text-right text-sm">
              <p className="font-medium tabular-nums">
                {data.quota.used} / {data.quota.limit}
              </p>
              <p className="text-xs text-muted-foreground">
                {Math.max(0, data.quota.limit - data.quota.used)} remaining
              </p>
            </div>
          )}
          <Button
            onClick={() => setShowForm((open) => !open)}
            disabled={!permitsChanges || atQuota}
          >
            <Plus className="mr-2 size-4" />
            New monitor
          </Button>
        </div>
      </div>

      <SubscriptionNotice />

      {monitors.isError && (
        <Alert variant="danger">
          Monitors could not be loaded: {getErrorMessage(monitors.error)}
        </Alert>
      )}

      {data && <MonitoringSummary monitors={data.monitors} />}

      {data && data.open_incidents > 0 && (
        <Alert variant="danger">
          <AlertTriangle className="size-4" />
          {data.open_incidents === 1
            ? "One monitor is currently down."
            : `${data.open_incidents} monitors are currently down.`}
        </Alert>
      )}

      {atQuota && data && (
        <Alert variant="warning">
          {onTrial
            ? `The free trial includes ${countOf(data.quota.limit, "monitor", "monitors")}, and you are using ${data.quota.limit === 1 ? "it" : "all of them"}. Choose a plan to add more, or delete a monitor to watch something else.`
            : `Your plan includes ${countOf(data.quota.limit, "monitor", "monitors")}, and you are using ${data.quota.limit === 1 ? "it" : "all of them"}. Upgrade your plan, or delete a monitor to watch something else.`}{" "}
          <Link to="/billing" className="font-medium underline underline-offset-2">
            {onTrial ? "See plans" : "Go to billing"}
          </Link>
          .
        </Alert>
      )}

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>New monitor</CardTitle>
            <CardDescription>
              Checked from outside your network, so this is what your visitors see.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {formError && <Alert variant="danger">{formError}</Alert>}

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="monitor-name">Name</Label>
                <Input
                  id="monitor-name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  How this monitor is identified in alerts.
                </p>
              </div>

              <div className="space-y-2">
                <Label>What to check</Label>
                {/* Wraps rather than staying on one row: five labels do not fit a panel this
                    width, and a shrunk-to-fit button is one nobody can read. */}
                <div className="flex flex-wrap gap-2">
                  {(Object.keys(KINDS) as MonitorFormKind[]).map((value) => (
                    <Button
                      key={value}
                      type="button"
                      variant={kind === value ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        setKind(value);
                        // Each kind has a sensible cadence of its own. Checking a registry every
                        // five minutes for a date that moves once a year is pointless traffic, and
                        // the customer should not have to know that to get it right.
                        setIntervalSeconds(KINDS[value].defaultInterval);
                      }}
                    >
                      {KINDS[value].label}
                    </Button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  {KINDS[kind].blurb}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="monitor-target">{KINDS[kind].addressLabel}</Label>
              {kind === "server" ? (
                <select
                  id="monitor-target"
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={target}
                  onChange={(event) => {
                    setTarget(event.target.value);
                    const chosen = (servers.data?.data ?? []).find(
                      (server) => server.id === event.target.value,
                    );
                    if (chosen && name.trim() === "") setName(chosen.name);
                  }}
                >
                  <option value="">
                    {(servers.data?.data ?? []).length === 0
                      ? "No servers yet"
                      : "Choose a server…"}
                  </option>
                  {(servers.data?.data ?? []).map((server) => (
                    <option key={server.id} value={server.id}>
                      {server.name} · {server.ssh_host}
                    </option>
                  ))}
                </select>
              ) : (
                <Input
                  id="monitor-target"
                  value={target}
                  onChange={(event) => setTarget(event.target.value)}
                />
              )}
              <p className="text-xs text-muted-foreground">
                {KINDS[kind].addressHelp}
              </p>
            </div>

            {kind === "server" && (
              <div className="space-y-3 rounded-md border p-3">
                <p className="text-sm font-medium">Alert when</p>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="space-y-1">
                    <Label htmlFor="threshold-disk">Disk above %</Label>
                    <Input
                      id="threshold-disk"
                      type="number"
                      min={0}
                      max={100}
                      value={thresholdDisk}
                      onChange={(event) => setThresholdDisk(Number(event.target.value))}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="threshold-memory">Memory above %</Label>
                    <Input
                      id="threshold-memory"
                      type="number"
                      min={0}
                      max={100}
                      value={thresholdMemory}
                      onChange={(event) => setThresholdMemory(Number(event.target.value))}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="threshold-load">Load per core above</Label>
                    <Input
                      id="threshold-load"
                      type="number"
                      min={0}
                      max={100}
                      step={0.5}
                      value={thresholdLoad}
                      onChange={(event) => setThresholdLoad(Number(event.target.value))}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="threshold-cpu">CPU above %</Label>
                    <Input
                      id="threshold-cpu"
                      type="number"
                      min={0}
                      max={100}
                      value={thresholdCpu}
                      onChange={(event) => setThresholdCpu(Number(event.target.value))}
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  0 switches a check off. CPU is off by default: a server at 100% CPU is usually a
                  server doing what you bought it for, and an alert that fires on normal work is one
                  you learn to ignore. A server that stops answering is always an alert.
                </p>
              </div>
            )}

            {(kind === "server" || kind === "http" || kind === "tcp" || kind === "ssl") && (
              <div className="space-y-2 rounded-md border p-3">
                {kind !== "server" && (
                  <div className="space-y-1">
                    <Label htmlFor="monitor-server">
                      Which server serves this (optional)
                    </Label>
                    <select
                      id="monitor-server"
                      className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                      value={serverId}
                      onChange={(event) => setServerId(event.target.value)}
                    >
                      <option value="">Not linked</option>
                      {(servers.data?.data ?? []).map((server) => (
                        <option key={server.id} value={server.id}>
                          {server.name} · {server.ssh_host}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={autoFix}
                    onChange={(event) => setAutoFix(event.target.checked)}
                  />
                  <span>
                    Try to fix this automatically
                    <span className="mt-1 block text-xs text-muted-foreground">
                      {(limits?.auto_fix.actions ?? [])
                        .map((action) => action.label.toLowerCase())
                        .join(", ") || "restart stopped services, reload pools, clear caches"}
                      . Nothing is rebooted, none of your data is deleted, no firewall rule is
                      changed and no release is rolled back. Every attempt is recorded on the
                      incident, and after{" "}
                      {limits?.auto_fix.max_per_day ?? 3} tries in a day the platform stops and asks
                      you to look.
                    </span>
                  </span>
                </label>
              </div>
            )}

            {kind === "http" && (
              <div className="space-y-2">
                <Label htmlFor="monitor-body">
                  Page must contain (optional)
                </Label>
                <Input
                  id="monitor-body"
                  value={bodyContains}
                  onChange={(event) => setBodyContains(event.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  A database error page still returns HTTP 200. A phrase from the working page
                  catches that.
                </p>
              </div>
            )}

            {isExpiryKind(kind) && (
              <div className="space-y-2">
                <Label htmlFor="monitor-warn-days">
                  Warn this many days ahead
                </Label>
                <Input
                  id="monitor-warn-days"
                  type="number"
                  min={1}
                  max={180}
                  value={warnDays}
                  onChange={(event) => setWarnDays(Number(event.target.value))}
                />
                <p className="text-xs text-muted-foreground">
                  Alerts while there is still time to renew.
                </p>
              </div>
            )}

            {kind === "dns" && (
              <>
                <div className="space-y-2">
                  <Label>Record type</Label>
                  <div className="flex flex-wrap gap-2">
                    {DNS_RECORD_TYPES.map((type) => (
                      <Button
                        key={type}
                        type="button"
                        variant={recordType === type ? "default" : "outline"}
                        size="sm"
                        onClick={() => setRecordType(type)}
                      >
                        {type}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="monitor-expected">Must contain</Label>
                  <Input
                    id="monitor-expected"
                    value={expectedValue}
                    onChange={(event) => setExpectedValue(event.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    The value the answer must include, such as the IP address for an A record or the
                    mail server name for an MX record. Matched as a substring, so formatting differences
                    do not cause false alarms.
                  </p>
                </div>
              </>
            )}

            <div className={kind === "server" ? "hidden space-y-2" : "space-y-2"}>
              <Label htmlFor="monitor-interval">Check every</Label>
              <select
                id="monitor-interval"
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={intervalSeconds}
                onChange={(event) =>
                  setIntervalSeconds(Number(event.target.value))
                }
              >
                {[60, 300, 900, 1800, 3600].map((seconds) => (
                  <option
                    key={seconds}
                    value={seconds}
                    disabled={
                      limits ? seconds < limits.min_interval_seconds : false
                    }
                  >
                    {seconds === 60
                      ? "1 minute"
                      : seconds < 3600
                        ? `${seconds / 60} minutes`
                        : `${seconds / 3600} hour`}
                  </option>
                ))}
              </select>
              {limits && (
                <p className="text-xs text-muted-foreground">
                  Confirmed after consecutive failures, so one blip is not an outage.
                </p>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => create.mutate()}
                disabled={
                  create.isPending || name.trim() === "" || target.trim() === ""
                }
              >
                {create.isPending && (
                  <Loader2 className="mr-2 size-4 animate-spin" />
                )}
                Create monitor
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {monitors.isLoading && (
        <p className="text-sm text-muted-foreground">Loading monitors…</p>
      )}

      {data && data.monitors.length === 0 && !showForm && (
        <Card>
          <CardContent className="py-10 text-center">
            <p className="text-sm text-muted-foreground">
              Nothing is being monitored yet.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Only once there is enough to search through. Two monitors do not need a filter bar, and
          showing one implies the page is more complicated than it is. */}
      {all.length > 3 && (
        <MonitorFilters
          search={search}
          onSearch={setSearch}
          status={status}
          onStatus={setStatus}
          counts={counts}
        />
      )}

      {all.length > 0 && visible.length === 0 && (
        <Card>
          <CardContent className="py-10 text-center">
            <p className="text-sm text-muted-foreground">
              No monitors match{" "}
              {search.trim() !== "" ? `“${search.trim()}”` : "that filter"}.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => {
                setSearch("");
                setStatus("all");
              }}
            >
              Clear filters
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="space-y-6">
        {groups.map((group) => (
          <div key={group.host} className="space-y-3">
            {/* One header per host. A customer asks "how is example.com doing?", not "how is
                monitor 7 doing?", and this is the line that answers it. */}
            <div className="flex flex-wrap items-center gap-2 px-1">
              <Globe className="text-muted-foreground size-4" />
              <span className="font-medium">{group.host}</span>
              <Badge variant={groupTone[group.status]}>
                {groupLabel[group.status]}
              </Badge>
              <span className="text-muted-foreground text-xs">
                {group.monitors.length} check
                {group.monitors.length === 1 ? "" : "s"}
              </span>
            </div>

            <div className="space-y-3">
              {group.monitors.map((monitor) => (
                <Card key={monitor.id}>
                  <CardContent className="space-y-3 py-4">
                    {/* Side by side from a small screen up, and never wrapped: in a wrapping row a
                        long address pushed the buttons onto a line of their own under the text. */}
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0 flex-1 space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            className="text-left font-medium hover:underline"
                            onClick={() =>
                              setExpanded((current) =>
                                current === monitor.id ? null : monitor.id,
                              )
                            }
                          >
                            {monitor.name}
                          </button>

                          {/*
                      A stale reading is drawn as a warning, never as its stored status. See the
                      component docblock — this is the single most important rule on the page.
                    */}
                          {monitor.is_stale ? (
                            <Badge variant="warning">
                              Not checked recently
                            </Badge>
                          ) : (
                            <Badge variant={statusVariant[monitor.status]}>
                              {monitor.status_label}
                            </Badge>
                          )}

                          {/* Beside "Up", never instead of it: a slow site is not a down site. */}
                          {!monitor.is_stale && monitor.is_slow && (
                            <Badge
                              variant="warning"
                              title="Answering, but slower than 15 seconds. Not an outage, and no alert is sent."
                            >
                              Slow
                            </Badge>
                          )}

                          <span className="text-xs text-muted-foreground">
                            {monitor.kind_label}
                          </span>

                          {/* Only when a fix could actually run: switched on AND a server to run it on. */}
                          {monitor.auto_fix_possible && (
                            <span
                              className="flex items-center gap-1 text-xs text-muted-foreground"
                              title="If this goes down, the platform tries the safe fixes before alerting you. Click the name to change it."
                            >
                              <Wrench className="size-3" />
                              Auto-fix on
                            </span>
                          )}

                          {monitor.certificate_days_remaining !== null &&
                            monitor.certificate_days_remaining <= 14 && (
                              <Badge variant="warning">
                                <ShieldAlert className="mr-1 size-3" />
                                Certificate expires in{" "}
                                {monitor.certificate_days_remaining} day
                                {monitor.certificate_days_remaining === 1
                                  ? ""
                                  : "s"}
                              </Badge>
                            )}
                        </div>

                        <p className="text-muted-foreground truncate font-mono text-xs">
                          {monitor.target}
                        </p>

                        {/* The shape of the recent history, which a 24-hour percentage cannot show:
                      four failures that each recovered still average 100%. */}
                        {!isExpiryKind(monitor.kind) && (
                          <StatusStrip
                            history={monitor.recent_health ?? []}
                            className="pt-1"
                          />
                        )}

                        {/* Figures on one line, each labelled. Previously one run-on sentence of
                      full stops and middots that had to be read rather than scanned. */}
                        <div className="text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1 pt-1 text-xs">
                          <span>
                            {monitor.last_checked_at
                              ? `Checked ${relativeTime(monitor.last_checked_at)}`
                              : "Not checked yet"}
                          </span>

                          {monitor.last_latency_ms !== null && (
                            <span>
                              <span className="text-foreground font-medium">
                                {monitor.last_latency_ms}
                              </span>{" "}
                              ms
                            </span>
                          )}

                          {/* An expiry monitor is asked one question — how long is left — and a
                        percentage does not answer it. Worse, these check daily, so five samples
                        is five days of "not enough data" on a monitor working perfectly. */}
                          {isExpiryKind(monitor.kind) ? (
                            <span>
                              {expiryLabel(monitor.certificate_days_remaining)}
                            </span>
                          ) : monitor.uptime_24h !== null &&
                            monitor.uptime_24h !== undefined ? (
                            <span>
                              <span className="text-foreground font-medium">
                                {formatUptimePercent(monitor.uptime_24h)}
                              </span>{" "}
                              over 24h
                            </span>
                          ) : (
                            <span>
                              Not enough checks for an uptime figure yet
                            </span>
                          )}
                        </div>

                        {monitor.status === "down" &&
                          monitor.last_detail !== "" && (
                            <p className="text-sm text-danger">
                              {monitor.last_detail}
                            </p>
                          )}
                      </div>

                      <div className="flex shrink-0 items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => check.mutate(monitor.id)}
                          disabled={
                            !permitsChanges ||
                            monitor.status === "paused" ||
                            check.isPending
                          }
                          title="Run a check now"
                        >
                          {check.isPending && check.variables === monitor.id ? (
                            <Loader2 className="size-4 animate-spin" />
                          ) : (
                            <RefreshCw className="size-4" />
                          )}
                        </Button>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setPaused.mutate({
                              id: monitor.id,
                              paused: monitor.status !== "paused",
                            })
                          }
                          disabled={setPaused.isPending}
                          title={
                            monitor.status === "paused" ? "Resume" : "Pause"
                          }
                        >
                          {monitor.status === "paused" ? (
                            <Play className="size-4" />
                          ) : (
                            <Pause className="size-4" />
                          )}
                        </Button>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPendingDelete(monitor)}
                          title="Remove"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </div>

                    {expanded === monitor.id && (
                      <MonitorDetailPanel monitorId={monitor.id} />
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>

      <NotificationTargets />

      <ConfirmDialog
        open={pendingDelete !== null}
        title={`Remove ${pendingDelete?.name ?? ""}?`}
        description="The check history and the record of past outages go with it. Nothing on your server is touched."
        confirmLabel="Remove monitor"
        destructive
        busy={remove.isPending}
        error={remove.error}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete) {
            remove.mutate(pendingDelete.id);
          }
        }}
      />
    </div>
  );
}
